import React from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Calendar } from "primereact/calendar";
import { Heading, T, ColorKeys, DownloadTemplate } from "../components/helpful";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  dateDifference,
  getMonthNumber,
  getStatusColor,
} from "../components/utilities";
import { DateObject } from "react-multi-date-picker";
import { TableSkeleton } from "../components/skeleton";
import {
  getHotelRoomStatus,
  getHotelRooms,
  getRoomStatusFiles,
} from "../api/api";

/* The RoomStatus class in JavaScript is a React component that manages the display of room status data
for a hotel, including filtering by date range and room selection. 
@author lionel ghogue ngondeke <lionelladrog@gmail.com>
*/
export default class RoomStatus extends React.Component {
  constructor(props) {
    super(props);
    const today = new Date();
    const options = { day: "numeric", month: "long", year: "numeric" };
    const formattedDate = today.toLocaleDateString("en-GB", options);
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const date = new Date(firstDay);

    date.setMonth(date.getMonth() + 1);
    date.setDate(0); // return the last day of month

    this.state = {
      pageTitle: <T value={"Room Status"} />,
      hotelID: 0,
      pageID: 0,
      roomID: 0,
      firstDay: firstDay,
      lastDay: date,
      isLoading: false,
      todayDate: formattedDate,
      dialogVisible: false,
      desc: (
        <>
          {"("} <T value={"Last update"} /> : {formattedDate} {" )"}
        </>
      ),
      date: null,
      dateFrom: firstDay,
      dateTo: date,
      roomStatus: [],
      hotelRooms: [],
      roomStatusFiles: [],
      fromIsGt: false,
      toIslr: false,
    };
    
    // this.roomStatus = hotelRoomStatus;

    this.headingTemplate = this.headingTemplate.bind(this);
    this.tableColumnTemplate = this.tableColumnTemplate.bind(this);
    this.dataTableTemplate = this.dataTableTemplate.bind(this);
    this.getRoomStatus = this.getRoomStatus.bind(this);
    this.daysOfMonth = this.daysOfMonth.bind(this);
    this.onHotelChange = this.onHotelChange.bind(this);
    this.handleRoomStatusFiles = this.handleRoomStatusFiles.bind(this);
    this.fileSizeTemplate = this.fileSizeTemplate.bind(this);
    this.onChange = this.onChange.bind(this);
    this.validateDate = this.validateDate.bind(this);
    this.clearFilter = this.clearFilter.bind(this);
  }

  async componentDidMount() {
    let pathname = window.location.pathname.split("/");
    let id = pathname[pathname.length - 1];

    if (id) {
      let rooms = await getHotelRooms(id);
      if (rooms.length > 0) {
        this.setState({ hotelRooms: rooms });
      }
      this.setState({ hotelID: id, pageID: id }, async () => {
        await this.getRoomStatus();
      });
    }
  }

  async componentDidUpdate(prevProps, prevState) {
    let pathname = window.location.pathname.split("/");
    let id = pathname[pathname.length - 1];
  
    if (id && id !== this.state.pageID) {
      try {
        let rooms = await getHotelRooms(id);
  
        if (rooms.length > 0) {
          this.setState(
            (prevState) => ({
              hotelRooms: rooms,
              hotelID: id,
              roomID: 0,
              dateFrom: prevState.firstDay,
              dateTo: prevState.lastDay,
              pageID: id, 
            }),
            async () => {
              await this.getRoomStatus();
            }
          );
        }
      } catch (error) {
        console.error("Error fetching rooms:", error);
      }
    }
  }
  

  async getRoomStatus() {
    /**
     * Retrieves the room status for a given hotel and date range.
     *
     * @return {Promise<void>} A promise that resolves when the room status has been updated.
     */
    let isValid = this.validateDate();

  
    if(!isValid){
      
      return;
    }
    if(this.state.hotelID===0){
      this.setState({ roomStatus: [], isLoading: false });
      return
    }

    let dateFrom = new DateObject(this.state.dateFrom)
      .format("Date:YYYY-MM-DD", ["Date"])
      .replace("Date:", "");
    let dateTo = new DateObject(this.state.dateTo)
      .format("Date:YYYY-MM-DD", ["Date"])
      .replace("Date:", "");

    this.setState({ isLoading: true });
    let resp = await getHotelRoomStatus(
      this.state.hotelID,
      dateFrom,
      dateTo,
      this.state.roomID
    );
    this.setState({ fromIsGt: false , toIslr:false});
    if (resp) {
      this.setState({ roomStatus: resp, isLoading: false });
    }
  }

  headingTemplate() {
    let date = new Date().toLocaleDateString(undefined, { year: "numeric" });
    return <T value={"Last update "} /> + ": " + date;
  }

  dataTableTemplate(roomstatus) {
    /**
     *  Returns a JSX element representing the room status.
     *
     *  If the room status is not valid, it returns a span element with the status.
     *  Otherwise, it returns a Tag element with the status color and value.
     *
     *  @param {object} roomstatus - The room status object.
     *  @return {JSX.Element} The JSX element representing the room status.
     */
    const validStatus = ["F", "R", "S", "AB", "A", "B", "-"];

    if (roomstatus !== undefined) {
      const { status, color, value } = getStatusColor(roomstatus);

      if (!validStatus.includes(status)) {
        return (
          <span key={status} className="room-code">
            {status}
          </span>
        );
      } else {
        return (
          <>
            <Tag
              key={status}
              value={value}
              className={color + "  table-room-status"}
            ></Tag>
          </>
        );
      }
    }
  }

  daysOfMonth() {
    /**
     * Calculates the difference in days between two given dates.
     *
     * @return {Array} An array of objects containing year, month and number of days.
     */
    let dateFrom = new DateObject(this.state.dateFrom)
      .format("Date:YYYY-MM-DD", ["Date"])
      .replace("Date:", "");
    let dateTo = new DateObject(this.state.dateTo)
      .format("Date:YYYY-MM-DD", ["Date"])
      .replace("Date:", "");

    return dateDifference(dateFrom, dateTo);
  }

  tableColumnTemplate(date) {
    /**
     * Generates an array of column objects for a table, based on the given limit and date.
     *
     * @param {Object} date - The date object containing the year, month, and numberOfDays.
     * @return {Array} An array of column objects.
     */
    const columns = [
      {
        field: "room_name",
        header: "CODE",
        frozen: true,
      },
    ];

    for (let i = 0; i < date.numberOfDays; i++) {
      columns.push({
        field: `${date.year.toString()}-${Number(
          getMonthNumber(date.month)
        ).toString()}-${(i + 1).toString().padStart(2, "0")}`,
        header: (i + 1).toString().padStart(2, "0"),
      });
    }
    return columns;
  }

  onHotelChange = async (e) => {
    this.setState({ hotelID: e.target.value });
    let rooms = await getHotelRooms(e.target.value);
    if (rooms.length > 0) {
      this.setState({ hotelRooms: rooms }, () => {
      
        this.getRoomStatus();
      });
    }
  };

  clearFilter=()=>{
    const today = new Date();
    const options = { day: "numeric", month: "long", year: "numeric" };
    const formattedDate = today.toLocaleDateString("en-GB", options);
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const date = new Date(firstDay);

    this.setState({hotelID: 0,roomID:0, dateFrom: firstDay,
      dateTo: date, hotelRooms: []})
  }

  validateDate = () => {
    const dateFrom = new Date(this.state.dateFrom);
    const dateTo = new Date(this.state.dateTo);
    let isValid = true

    if (dateFrom.getTime() > dateTo.getTime()) {
     
      this.setState({fromIsGt:true});
     isValid = false

    }
    if (dateTo.getTime() < dateFrom.getTime()) {
      this.setState({toIslr:true})
      isValid = false
    }
    return isValid;
  };

  onChange = (e) => {
    let value = e.target.value;
    const name = e.target.name;

    if (name === "dateTo") {
      value.setMonth(value.getMonth() + 1);

      value.setDate(0);
    }
    this.setState(
      {
        [name]: value,
      },
      async () => {
        if (name === "hotelID") {
          let rooms = await getHotelRooms(value);
          if (rooms.length > 0) {
            this.setState({ hotelRooms: rooms });
          }
        }
        this.getRoomStatus();
      }
    );
  };

  handleRoomStatusFiles = async (year, month) => {
    let convertedMonth = Number(getMonthNumber(month));

    let files = await getRoomStatusFiles(year, convertedMonth);

    if (files) {
      this.setState({ roomStatusFiles: files });
    }

    this.setState({ dialogVisible: true });
  };

  fileSizeTemplate = (data) => {
    let size = Number(data.fileSize) / 1024;
    return <>{(size / 1024).toFixed(2)} MB</>;
  };

  subHeading = () => {
    return <>{this.state.desc}</>;
  };

  render() {
    const hotels = this.props.hotels;

    return (
      <div>
        <div className="">
          <div className="sticky-top">
            <Heading heading={this.state.pageTitle} Desc={this.subHeading} />
          </div>
          <div className="main-content">
            <Dialog
              header=""
              visible={this.state.dialogVisible}
              style={{ width: "65vw" }}
              maximizable
              modal
              contentStyle={{ height: "400px" }}
              onHide={() => this.setState({ dialogVisible: false })}
            >
              <DataTable
                value={this.state.roomStatusFiles}
                scrollable
                stripedRows
                sortMode="single"
                sortField="dateUploaded"
                scrollHeight="flex"
                className="download-Room-status-dialog"
                tableStyle={{ minWidth: "50rem" }}
              >
                <Column field="filename" header="File Name"></Column>
                <Column
                  field="fileSize"
                  header="File Size"
                  body={(data) => this.fileSizeTemplate(data)}
                ></Column>
                <Column field="fileType" header="File Type"></Column>
                <Column field="dateUploaded" header="Uploaded Date"></Column>
                <Column
                  field=""
                  header=""
                  body={(data) => <DownloadTemplate item={data} />}
                ></Column>
              </DataTable>
            </Dialog>

            <Row className="filter g-2 mt-4">
              <div>
                <Form className="row g-2">
                  <Col sm={1} className="filter-icon-block">
                    <div className="col mx-auto">
                      <h6>
                        <T value={"Filters"} /> :
                      </h6>
                      <i className="bi bi-sliders filter-icon"></i>
                    </div>
                  </Col>
                  <Col sm={10}>
                    <Row className="g-2">
                      <Form.Group className="col-12 col-sm-3 col-md-3 col-xl-3">
                        <Form.Label className="">
                          <T value={"Hotels"} /> :
                        </Form.Label>
                        <Form.Select
                          aria-label=""
                          name="hotelID"
                          id="hotel"
                          // disabled={true}
                          placeholder="Select Hotel"
                          value={this.state.hotelID}
                          onChange={(e) => {
                            this.onChange(e);
                          }}
                        >
                          <option value="0" defaultValue>
                            <T value={"Select Hotel"} />
                          </option>
                          {hotels.map((hotel, index) => {
                            return (
                              <option value={hotel.hotel_id} key={index}>
                                {hotel.hotel_name}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="col-12 col-sm-3 col-md-3 col-xl-3">
                        <Form.Label className="">
                          <T value={"Rooms"} /> :
                        </Form.Label>
                        <Form.Select
                          aria-label=""
                          name="roomID"
                          id="room"
                          placeholder="Select Room"
                          onChange={(e) => {
                            this.onChange(e);
                          }}
                        >
                          <option value="0" defaultValue>
                            <T value={"Select Room"} />
                          </option>
                          {this.state.hotelRooms.map((room, index) => {
                            return (
                              <option value={room.room_id} key={index}>
                                {room.room_name}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="col-12 col-sm-3 col-md-3 col-xl-3">
                        <Form.Label className="d-block">
                          <T value={"From"} /> :
                        </Form.Label>
                        <div className="field p-fluid">
                          <Calendar
                            view="month"
                            // maxDate={this.state.dateTo}
                            dateFormat="mm/yy"
                            name="dateFrom"
                            className={`col-12 ${
                              this.state.fromIsGt ? "p-invalid" : ""
                            }  mr-2`}
                            aria-describedby="dateFrom-help"
                            value={this.state.dateFrom}
                            onChange={(e) => {
                              this.onChange(e);
                            }}
                          />
                          {this.state.fromIsGt ? (
                            <small id="dateFrom-help" className="p-error">
                              The start date must be earlier than the end date.
                            </small>
                          ) : null}
                        </div>
                      </Form.Group>
                      <Form.Group className="col-12 col-sm-3 col-md-3 col-xl-3">
                        <Form.Label className="d-block">
                          <T value={"To"} /> :
                        </Form.Label>
                        <div className="field p-fluid">
                          <Calendar
                            view="month"
                            // minDate={this.state.dateFrom}
                            dateFormat="mm/yy"
                            name="dateTo"
                            value={this.state.dateTo}
                             aria-describedby="dateTo-help"
                            onChange={(e) => {
                              this.onChange(e);
                            }}
                            className={`col-12 ${
                              this.state.toIslr ? "p-invalid" : ""
                            } `}
                          />
                          {this.state.toIslr ? (
                            <small id="dateTo-help" className="p-error">
                             The end date must be greater than the start date.
                            </small>
                          ) : null}
                        </div>
                      </Form.Group>
                    </Row>
                  </Col>
                  <Col sm={1} className="">
                  <div className="row g-2">
                    <div className="col-12 col-sm-6 col-md-6 ">
                    <Button
                      variant="dark"
                      className="col-12 d-flex justify-content-center"
                      onClick={(e) => this.getRoomStatus()}
                    >
                      <i className="bi bi-caret-right-fill"></i>
                    </Button>
                    </div>
                    <div className="col-12 col-sm-6 col-md-6 ">
                    <Button
                      variant="secondary"
                      className="col-12 d-flex justify-content-center bcb-btn-ternary text-small"
                      onClick={(e) => this.clearFilter()}
                    >
                      Clear
                     
                    </Button>
                    </div>

                  </div>
                    
                    
                  </Col>
                </Form>
              </div>
            </Row>

            <Row className="">
              <div className="">
                <Accordion
                  expandIcon={"bi bi-caret-up-fill "}
                  collapseIcon={"bi bi-caret-down-fill"}
                  multiple={false}
                  activeIndex={0}
                  className="mt-4 room-status-accordion"
                >
                  <AccordionTab
                    key="color key"
                    header={
                      <span className="flex align-items-center gap-2 w-full">
                        <T value={"Room status As At"} /> {this.state.todayDate}
                        <span className="color-key-title">
                          <span className="mr-4">
                            <i className="bi bi-circle-fill color-key-title-tag text-yellow"></i>
                            <i className="bi bi-circle-fill color-key-title-tag text-blue"></i>
                            <i className="bi bi-circle-fill color-key-title-tag text-red "></i>
                          </span>

                          <T value={"color key"} />
                        </span>
                      </span>
                    }
                    disabled={""}
                  >
                    <ColorKeys />
                  </AccordionTab>
                </Accordion>
              </div>
            </Row>

            <Row className="">
              {!this.state.isLoading ? (
                this.daysOfMonth().map((month, i) => {
                  return (
                    <div key={i}>
                      <div>
                        <div className="d-flex justify-content-between align-items-center download-file">
                          <h3>{`${month.month} ${month.year}`}</h3>
                          <span className="download-file-icon-block">
                            <button
                              className="bcb-btn bcb-btn-small bcb-btn-ternary px-3"
                              onClick={() =>
                                this.handleRoomStatusFiles(
                                  month.year,
                                  month.month
                                )
                              }
                            >
                              <T value={"Download"} />
                            </button>
                          </span>
                        </div>
                      </div>

                      <div className="table-responsive ">
                        {this.state.roomStatus.length > 0 ||
                        !this.state.isLoading ? (
                          <DataTable
                            value={this.state.roomStatus}
                            scrollable
                            resizableColumns
                            showGridlines
                            emptyMessage="No Room status found."
                            size={"small"}
                            id="room-status-table"
                            className="room-status-table mb-5"
                          >
                            {this.tableColumnTemplate(month).map((item, i) => {
                              return (
                                <Column
                                  frozen={item.frozen ?? false}
                                  key={item.header + item.id}
                                  field={item.field}
                                  header={item.header}
                                  className={i === 0 ? "text-bold" : ""}
                                  body={(data) =>
                                    this.dataTableTemplate(data[item.field])
                                  }
                                ></Column>
                              );
                            })}
                          </DataTable>
                        ) : (
                          <TableSkeleton />
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <TableSkeleton />
              )}
            </Row>
          </div>
        </div>
      </div>
    );
  }
}
